import { useQuery } from '@tanstack/react-query';
import { listOrganizations, listTrials } from '@/api/platform/generated/sdk.gen';
import type {
  PermissionResourcesContext,
  ResourceSectionData,
} from '@/components/permissions/types';

export function usePermissionResources(ctx: PermissionResourcesContext): ResourceSectionData[] {
  const { mode, accessibleResourceIds } = ctx;
  const isViewMode = mode === 'view';
  const hasResourcePerms = accessibleResourceIds.size > 0;

  // In view mode with no resource permissions, skip fetching entirely
  const orgsQuery = useQuery({
    queryKey: ['organizations', 'for-permissions', mode],
    queryFn: async () => {
      const { data } = await listOrganizations({
        query: { page: 1, per_page: 200 },
        throwOnError: true,
      });
      return data;
    },
    enabled: !isViewMode || hasResourcePerms,
    staleTime: 5 * 60 * 1000,
    select: (data) => data?.items ?? [],
  });

  // In view mode, only fetch trials for orgs the user has access to
  const filteredOrgs = isViewMode
    ? (orgsQuery.data ?? []).filter((o) => accessibleResourceIds.has(o.id))
    : (orgsQuery.data ?? []);

  const trialsQuery = useQuery({
    queryKey: ['trials', 'for-permissions', mode, filteredOrgs.map((o) => o.id)],
    queryFn: async () => {
      const results = await Promise.all(
        filteredOrgs.map((org) =>
          listTrials({
            path: { org_id: org.id },
            query: { page: 1, per_page: 200 },
            throwOnError: true,
          }).then((res) => (res.data?.items ?? []).map((t) => ({ ...t, _orgId: org.id })))
        )
      );
      return results.flat();
    },
    enabled: filteredOrgs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // In view mode, filter items to only those the user has permissions on
  const orgItems = isViewMode
    ? (orgsQuery.data ?? []).filter((o) => accessibleResourceIds.has(o.id))
    : (orgsQuery.data ?? []);

  const trialItems = isViewMode
    ? (trialsQuery.data ?? []).filter((t) => accessibleResourceIds.has(t.id))
    : (trialsQuery.data ?? []);

  return [
    {
      resourceType: 'organization',
      items: orgItems.map((o) => ({
        id: o.id,
        name: o.name,
        resourceType: 'organization',
      })),
      isLoading: orgsQuery.isLoading,
    },
    {
      resourceType: 'trial',
      items: trialItems.map((t) => ({
        id: t.id,
        name: t.name,
        resourceType: 'trial',
        parentId: t._orgId,
        parentResourceType: 'organization',
      })),
      isLoading: trialsQuery.isLoading,
    },
  ];
}
